import { thermalReceiptHtml, type ReceiptData } from "@astroplanet/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import { previewAssets } from "../../lib/receiptAssets";

const DOTS = { 58: 384, 80: 576 } as const;

/**
 * The receipt exactly as the printer will produce it: the very same layout function the Electron POS rasterises, drawn at the printer's dot width
 * and scaled to fit. Black on white, Cairo font, right-to-left for Arabic. (Dots on the paper are what you see; the thermal head adds nothing.)
 */
export function ReceiptPreview({ data, paperWidth, maxWidth = 380 }: { data: ReceiptData; paperWidth: 58 | 80; maxWidth?: number }) {
  const holder = useRef<HTMLDivElement>(null);
  const [avail, setAvail] = useState(maxWidth);
  const dots = DOTS[paperWidth];
  const html = useMemo(() => thermalReceiptHtml(data, dots, previewAssets()), [data, dots]);
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(600);
  const scale = Math.max(0.3, Math.min(1, Math.min(maxWidth, avail) / dots));

  // fit the phone-sized panes too: measure the room we really have
  useEffect(() => {
    const el = holder.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setAvail(Math.max(120, el.clientWidth - 32)));
    ro.observe(el);
    setAvail(Math.max(120, el.clientWidth - 32));
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const f = frame.current;
    if (!f) return;
    const measure = () => {
      const h = f.contentDocument?.documentElement.scrollHeight;
      if (h) setHeight(h);
    };
    f.addEventListener("load", measure);
    const t = setTimeout(measure, 350); // fonts finish after load
    return () => { f.removeEventListener("load", measure); clearTimeout(t); };
  }, [html]);

  return (
    <div ref={holder} className="w-full">
    <div className="mx-auto rounded-xl bg-gray-200 p-4 shadow-inner" style={{ width: dots * scale + 32 }} data-testid="receipt-preview">
      <div className="mx-auto overflow-hidden bg-white shadow-lg" style={{ width: dots * scale, height: height * scale + 12, clipPath: "polygon(0 0,100% 0,100% calc(100% - 8px),97% 100%,94% calc(100% - 8px),91% 100%,88% calc(100% - 8px),85% 100%,82% calc(100% - 8px),79% 100%,76% calc(100% - 8px),73% 100%,70% calc(100% - 8px),67% 100%,64% calc(100% - 8px),61% 100%,58% calc(100% - 8px),55% 100%,52% calc(100% - 8px),49% 100%,46% calc(100% - 8px),43% 100%,40% calc(100% - 8px),37% 100%,34% calc(100% - 8px),31% 100%,28% calc(100% - 8px),25% 100%,22% calc(100% - 8px),19% 100%,16% calc(100% - 8px),13% 100%,10% calc(100% - 8px),7% 100%,4% calc(100% - 8px),1% 100%,0 calc(100% - 8px))" }}>
        <iframe ref={frame} title="receipt preview" srcDoc={html} sandbox="allow-same-origin" scrolling="no" style={{ width: dots, height, border: 0, transform: `scale(${scale})`, transformOrigin: "top left", display: "block" }} />
      </div>
    </div>
    </div>
  );
}
